import { api } from '../api/client';

/**
 * Notifications, on **this device**.
 *
 * A push subscription belongs to a browser profile, not to an account: the same
 * account read from a phone and from a laptop has to ask on each of them, and
 * turning it off here says nothing about there. Everything in this module is
 * therefore about the browser in front of somebody, and the only thing it sends
 * to the server is the endpoint that browser minted.
 *
 * Nothing here asks for permission on its own. `requestPermission()` is called
 * from `enablePush` and from nowhere else, and `enablePush` is called from a
 * click — a permission prompt on page load is the one interaction browsers now
 * penalise, and it is also simply rude.
 */

/**
 * The five states the toggle can be in, all of which are drawn.
 *
 * `unsupported` covers three different situations that a person can do nothing
 * different about: a browser with no `PushManager`, a browser where the service
 * worker is not registered — which is every browser in development, where
 * registration is deliberately skipped — and iOS Safari before the application
 * has been added to the Home Screen.
 */
export type PushState = 'unsupported' | 'denied' | 'off' | 'on';

/**
 * The application server's key, from base64url to the bytes `subscribe` wants.
 *
 * `applicationServerKey` takes a `BufferSource` and nothing else — it refuses
 * the string the server sent, which is why this exists at all. The padding has
 * to be put back before `atob` will look at it, and `-`/`_` swapped for `+`/`/`:
 * base64url is what travels in JSON, and `atob` only knows the other one.
 */
export function base64UrlToBytes(value: string): Uint8Array<ArrayBuffer> {
  const padded = value.padEnd(value.length + ((4 - (value.length % 4)) % 4), '=');
  const binary = atob(padded.replace(/-/g, '+').replace(/_/g, '/'));
  // Backed by an `ArrayBuffer` named as such rather than by the default
  // `ArrayBufferLike`, which also admits a `SharedArrayBuffer` and is therefore
  // not the `BufferSource` `subscribe` will take.
  const bytes = new Uint8Array(new ArrayBuffer(binary.length));
  for (let index = 0; index < binary.length; index++) bytes[index] = binary.charCodeAt(index);
  return bytes;
}

/**
 * How long to wait for a worker that is still registering. Long enough for a
 * first visit on a slow connection, short enough that a browser which will never
 * have one is not left drawing nothing.
 */
const READY_TIMEOUT_MS = 3000;

/**
 * The registration that would carry a notification, or `null` when there is
 * none.
 *
 * Two steps, and both are needed. `getRegistration()` answers at once for every
 * visit after the first, which is nearly all of them.
 *
 * When it answers with nothing, that can mean two opposite things, and telling
 * them apart is the whole of the rest of this function. It can mean the worker
 * is registering right now: `registerServiceWorker` does it on `load`, and this
 * component mounts before that — a first visit would otherwise be told its
 * browser cannot ring, which is both wrong and the visit where somebody decides
 * whether the feature exists. Or it can mean nothing will ever register, which
 * is every browser under `pnpm dev`.
 *
 * `navigator.serviceWorker.ready` distinguishes them, but only in one direction:
 * it settles when a worker becomes active and **never settles at all** when none
 * is registered. So it is raced against a deadline rather than awaited — a
 * toggle waiting on a promise that cannot resolve is a spinner with no end.
 */
async function registration(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return null;
  if (typeof Notification === 'undefined') return null;

  const existing = await navigator.serviceWorker.getRegistration();
  if (existing) return existing;

  return await Promise.race([
    navigator.serviceWorker.ready,
    new Promise<null>((resolve) => setTimeout(() => resolve(null), READY_TIMEOUT_MS)),
  ]);
}

export async function pushState(): Promise<PushState> {
  const worker = await registration();
  if (!worker) return 'unsupported';
  // Checked before the subscription: a browser that has been told to refuse can
  // still be holding a subscription from before it was, and reporting that as
  // "on" would promise a notification that will never be shown.
  if (Notification.permission === 'denied') return 'denied';
  return (await worker.pushManager.getSubscription()) ? 'on' : 'off';
}

/**
 * What came of asking, which is **not** the same question as what state the
 * toggle is now in — and conflating the two is what made a button appear to do
 * nothing.
 *
 * `dismissed` is the one worth naming. A browser is not obliged to put the
 * permission request on screen: Chrome in particular replaces it with a small
 * icon in the address bar once somebody has waved such a prompt away, and
 * `requestPermission()` then resolves to `default` **immediately**, having asked
 * nobody anything. Reported as "still off", that is a control that visibly does
 * not work; reported as itself, it is a sentence naming where the answer now
 * has to be given.
 */
export type EnableOutcome = 'on' | 'denied' | 'dismissed' | 'unsupported' | 'failed';

/**
 * Asks the browser, then subscribes, then tells the server.
 *
 * **Only ever called from a real click.** Both because a browser will refuse a
 * prompt that is not, and because being asked before having seen what the
 * application does is how somebody decides to say no once and for all.
 */
export async function enablePush(publicKey: string): Promise<EnableOutcome> {
  const worker = await registration();
  if (!worker) return 'unsupported';

  const permission = await Notification.requestPermission();
  if (permission === 'denied') return 'denied';
  // Still undecided after asking: the browser did not put the question on
  // screen, or it was waved away. Either way nobody has said no, so this is not
  // `denied` — pressing again after allowing it in the address bar works.
  if (permission !== 'granted') return 'dismissed';

  const subscription = await worker.pushManager.subscribe({
    // Required, and honest: every push this application sends shows a
    // notification. A browser refuses the subscription outright without it.
    userVisibleOnly: true,
    applicationServerKey: base64UrlToBytes(publicKey),
  });

  const { endpoint, keys } = subscription.toJSON() as {
    endpoint?: string;
    keys?: { p256dh?: string; auth?: string };
  };
  if (!endpoint || !keys?.p256dh || !keys.auth) {
    // A subscription the server could never encrypt for. Undone here rather than
    // left in place: a browser holding one this instance does not know about
    // would report "on" for ever without ringing once.
    await subscription.unsubscribe();
    return 'failed';
  }

  await api.push.subscribe({ endpoint, keys: { p256dh: keys.p256dh, auth: keys.auth } });
  return 'on';
}

/**
 * Tells the server first, then the browser.
 *
 * That order is the one that fails safely. The row going first means the worst
 * outcome is a browser still holding a subscription nothing will ever push to;
 * the other way round, a failed call would leave the server ringing a device
 * that has thrown its keys away — and the only thing that would eventually clear
 * that is the 410 the push service returns, weeks later.
 */
export async function disablePush(): Promise<PushState> {
  const worker = await registration();
  if (!worker) return 'unsupported';

  const subscription = await worker.pushManager.getSubscription();
  if (!subscription) return 'off';

  await api.push.unsubscribe(subscription.endpoint);
  await subscription.unsubscribe();
  return 'off';
}
